import React, { useState } from 'react';
import Button from './Button';
import Tag from './Tag';
import defaultImage from '../images/lineup-default.png';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { ExportCta } from '../context/DataContext';

import './Card.scss';
import { useApp } from '../context/AppContext';

interface CardPropsBase {
  title: string;
  subtitle: string | string[];
  description?: string;
  image?: string;
  tags?: Array<React.ReactElement<typeof Tag>>;
  callToActions?: ExportCta[];
  isFavorite?: boolean;
  unread?: boolean;
  detailed?: boolean;
  onFavoriteClick?: () => void;
}

type CardProps = React.HTMLAttributes<HTMLDivElement> & CardPropsBase;

function Card(props: CardProps) {
  const {
    title,
    subtitle,
    description,
    image,
    tags,
    callToActions,
    isFavorite,
    unread,
    detailed,
    onFavoriteClick,
    className,
    ...rest
  } = props;

  const { config } = useApp();
  const [imageError, setImageError] = useState(false);

  const heartClasses = ['text-xl font-semibold'];

  if (isFavorite) {
    heartClasses.push('bg-black', 'text-white card-favorite-active');
  } else {
    heartClasses.push('text-black card-favorite');
  }

  const subtitles = typeof subtitle == 'string' ? [subtitle] : subtitle || [];

  const classes = ['bg-white border-t border-slate-300 content-card'];

  if (className) {
    classes.push(className);
  }

  const favoritesEnabled = isFavorite !== undefined && onFavoriteClick && !config.hideFavorites;

  const showCallToActions =
    (favoritesEnabled && detailed) || (callToActions && callToActions.length > 0);

  return (
    <div {...rest} className={classes.join(' ')}>
      {image && (
        <div className="aspect-ratio-hack overflow-hidden">
          <LazyLoadImage
            className="full inner"
            wrapperClassName="full bg-slate-300"
            loading="lazy"
            src={imageError ? defaultImage : image}
            onError={() => setImageError(true)}
          />
        </div>
      )}
      <div className="relative p-4" style={{ zIndex: 1 }}>
        {unread && (
          <div
            style={{
              fontSize: '0.6rem',
              lineHeight: 0,
              top: '10px',
              right: '10px',
              width: '10px',
              height: '10px',
            }}
            className="absolute bg-blue-500 rounded-full"
          />
        )}
        {(tags || []).length > 0 && (
          <div className={`flex flex-wrap mb-2${image ? ' -mt-7' : ''}`}>
            {(tags || []).map((tag, index) => (
              <div className="mr-1" key={index}>
                {tag}
              </div>
            ))}
          </div>
        )}
        <div className="flex">
          <div className="flex-grow">
            <div className="grid gap-y-1">
              <div
                className={`leading-snug font-semibold${
                  detailed ? ' text-xl' : ''
                } card-html card-title`}
                dangerouslySetInnerHTML={{ __html: title }}
              ></div>
              {subtitles.length > 0 && (
                <div className="leading-snug">
                  {subtitles.map((subtitle, index) => (
                    <div
                      key={index}
                      className="card-html card-subtitle"
                      dangerouslySetInnerHTML={{ __html: subtitle }}
                    ></div>
                  ))}
                </div>
              )}
              {description && (
                <div
                  className="mt-4 leading-snug card-html card-description"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
            </div>
          </div>
          {favoritesEnabled && !detailed && (
            <div className="grid items-center pl-8">
              <Button
                outline="border-black"
                shape="circle"
                size={20}
                icon={isFavorite ? ['fass', 'heart'] : ['far', 'heart']}
                className={heartClasses.join(' ')}
                onClick={() => onFavoriteClick?.()}
              />
            </div>
          )}
        </div>
        {showCallToActions && (
          <div className={`mt-3 text-sm ${detailed ? 'flex flex-col items-start space-y-2' : ''}`}>
            {favoritesEnabled && detailed && (
              <Button
                onClick={() => onFavoriteClick?.()}
                outline="border-black"
                shape="oval"
                className={
                  isFavorite ? 'bg-black text-white card-favorite-active' : 'card-favorite'
                }
                icon={isFavorite ? ['fass', 'heart'] : ['far', 'heart']}
                iconPosition="left"
              >
                {isFavorite ? 'Unfavorite' : 'Favorite'}
              </Button>
            )}
            <div className="flex flex-wrap gap-2">
              {(callToActions || []).map((e, index) => (
                <a key={index} href={e.link} target={e.target}>
                  <Button
                    outline="border-blue-500"
                    shape="oval"
                    className="text-blue-500 text-sm card-cta"
                  >
                    {e.label}
                  </Button>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Card;
